import React, { useState, useEffect, useMemo } from 'react'
import { Select, Spin, Alert } from 'antd'
import { ApiRequest } from '../../../services/api/api'

const { Option } = Select

/**
 * PackagingTypeCascadeSelect - 拆分输入的包装类型选择器 (版本: 2.0 - 修复数据显示)
 *
 * 设计参考：
 * - Carton Qty（数量）/ Sleeve Qty（规格）两个下拉框
 * - 中间用斜杠分隔
 * - 下方显示组合结果预览
 *
 * 使用示例：
 * <PackagingTypeCascadeSelect
 *   value={packagingTypeId}
 *   onChange={(id) => setPackagingTypeId(id)}
 *   disabled={false}
 * />
 */
console.log('PackagingTypeCascadeSelect component loaded v2.0')

interface PackagingTypeData {
  packagingTypeId: number
  quantity: number
  sleevePkt: number
  packagingCategory: string
}

interface PackagingTypeCascadeSelectProps {
  value?: number | null
  onChange?: (value: number | null) => void
  disabled?: boolean
  style?: React.CSSProperties
  inlinePreview?: boolean
  /** 加载状态回调：父组件可用于禁用保存按钮，直到包装类型数据加载完成 */
  onLoadingChange?: (loading: boolean) => void
}

const PackagingTypeCascadeSelect: React.FC<PackagingTypeCascadeSelectProps> = (props) => {
  const [packagingData, setPackagingData] = useState<PackagingTypeData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cartonQty, setCartonQty] = useState<number | undefined>()
  const [sleeveQty, setSleeveQty] = useState<number | undefined>()

  // ── 获取所有包装类型数据 ─────────────────────────────────────────────
  useEffect(() => {
    props.onLoadingChange?.(true)
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)
        const result = await ApiRequest({
          url: 'PackagingType/GetAllPackagingType',
          method: 'get',
          isShowSpinner: false
        })
        setPackagingData(result?.data?.data ?? [])
      } catch (err) {
        setError('Failed to load packaging types')
        console.error('Error fetching packaging types:', err)
      } finally {
        setLoading(false)
        props.onLoadingChange?.(false)
      }
    }
    fetchData()
    return () => {
      props.onLoadingChange?.(false)
    }
  }, [])

  // ── 同步内部状态与外部 value ─────────────────────────────────────────
  useEffect(() => {
    const id = props.value
    console.log('PackagingTypeCascadeSelect sync effect:', { id, packagingDataLength: packagingData.length, currentCartonQty: cartonQty, currentSleeveQty: sleeveQty })

    if (id && packagingData.length > 0) {
      const found = packagingData.find(d => d.packagingTypeId === id)
      console.log('PackagingTypeCascadeSelect found data:', { id, found })
      if (found) {
        // 只有当前没有值或者值不匹配时才更新（避免在编辑过程中重置）
        if (!cartonQty || cartonQty !== found.quantity || !sleeveQty || sleeveQty !== found.sleevePkt) {
          setCartonQty(found.quantity)
          setSleeveQty(found.sleevePkt)
        }
        return
      }
    }
    // 只有在没有外部值且当前也没有值时才清空（避免在编辑过程中被清空）
    if (!id && !cartonQty && !sleeveQty) {
      // 已经是空状态，不需要操作
    }
  }, [props.value, packagingData])

  // ── 获取当前包装类型的显示信息 ─────────────────────────────────────────
  const currentPackagingInfo = useMemo(() => {
    if (!props.value || packagingData.length === 0) return null
    const found = packagingData.find(d => d.packagingTypeId === props.value)
    if (found) {
      const category = found.packagingCategory || 'Carton'
      const sleeveCount = found.quantity / found.sleevePkt
      return {
        ...found,
        displayLabel: `${found.quantity} / ${category} / ${found.sleevePkt} * ${sleeveCount}`
      }
    }
    return null
  }, [props.value, packagingData])

  // ── Carton Qty 选项（去重、排序）───────────────────────────────────────
  const cartonQtyOptions = useMemo(() => {
    const unique = Array.from(new Set(packagingData.map(d => d.quantity)))
      .filter(v => v > 0)
      .sort((a, b) => a - b)
    return unique.map(v => ({ label: String(v), value: v }))
  }, [packagingData])

  // ── Sleeve Qty 选项（根据 Carton Qty 过滤）────────────────────────────
  const sleeveQtyOptions = useMemo(() => {
    if (!cartonQty) return []
    const unique = Array.from(
      new Set(
        packagingData
          .filter(d => d.quantity === cartonQty && d.sleevePkt > 0 && cartonQty % d.sleevePkt === 0)
          .map(d => d.sleevePkt)
      )
    ).sort((a, b) => a - b)
    return unique.map(v => ({ label: String(v), value: v }))
  }, [packagingData, cartonQty])

  // ── 预览组合结果 ───────────────────────────────────────────────────────
  const previewLabel = useMemo(() => {
    if (!cartonQty || !sleeveQty) {
      console.log('previewLabel empty:', { cartonQty, sleeveQty })
      return ''
    }
    const match = packagingData.find(d => d.quantity === cartonQty && d.sleevePkt === sleeveQty)
    const category = match?.packagingCategory ?? 'Carton'
    const sleeveCount = cartonQty / sleeveQty
    const label = `${cartonQty} / ${category} / ${sleeveQty} * ${sleeveCount}`
    console.log('previewLabel generated:', { cartonQty, sleeveQty, match, label })
    return label
  }, [cartonQty, sleeveQty, packagingData])

  // ── 提交选择的包装类型 ID ───────────────────────────────────────────
  const commitSelection = (row: PackagingTypeData | null) => {
    props.onChange?.(row ? row.packagingTypeId : null)
  }

  // ── 处理 Carton Qty 变化 ────────────────────────────────────────────────
  const handleCartonQtyChange = (value: number) => {
    console.log('handleCartonQtyChange:', value)
    setCartonQty(value)
    setSleeveQty(undefined)
    // 不要立即提交 null，等待用户选择 Sleeve Qty
  }

  const handleCartonQtyClear = () => {
    setCartonQty(undefined)
    setSleeveQty(undefined)
    commitSelection(null)
  }

  // ── 处理 Sleeve Qty 变化 ────────────────────────────────────────────────
  const handleSleeveQtyChange = (value: number) => {
    setSleeveQty(value)
    if (cartonQty) {
      const match = packagingData.find(d => d.quantity === cartonQty && d.sleevePkt === value)
      commitSelection(match ?? null)
    }
  }

  const handleSleeveQtyClear = () => {
    setSleeveQty(undefined)
    commitSelection(null)
  }

  // ── 加载状态（如果有当前值，显示当前值）──────────────────────────────────
  if (loading && packagingData.length === 0) {
    if (props.value) {
      return (
        <div style={{ padding: '8px 0', textAlign: 'center' }}>
          <Spin size="small" style={{ marginRight: 8 }} />
          <span style={{ color: '#888', fontSize: 12 }}>Loading packaging types...</span>
        </div>
      )
    }
    return (
      <div style={{ padding: '8px 0', textAlign: 'center' }}>
        <Spin size="small" />
      </div>
    )
  }

  // ── 错误状态 ──────────────────────────────────────────────────────────────
  if (error) {
    return (
      <Alert
        message={error}
        type="error"
        showIcon
        style={{ marginBottom: 8 }}
      />
    )
  }

  // ── 无数据状态 ────────────────────────────────────────────────────────────
  if (packagingData.length === 0) {
    return (
      <Select
        disabled
        placeholder="暂无可用的包装类型"
        style={{ width: '100%', ...props.style }}
      />
    )
  }

  // ── 渲染拆分输入界面 ───────────────────────────────────────────────────────
  const wrapperStyle: React.CSSProperties = {
    width: '100%',
    ...props.style
  }

  const previewStyle: React.CSSProperties = {
    marginTop: 8,
    padding: '6px 12px',
    background: '#f6f8ff',
    border: '1px solid #adc6ff',
    borderRadius: 6,
    fontSize: 12,
    color: '#555',
    display: 'flex',
    alignItems: 'center',
    gap: 6
  }

  const inlinePreviewStyle: React.CSSProperties = {
    padding: '4px 10px',
    background: '#f6f8ff',
    border: '1px solid #adc6ff',
    borderRadius: 6,
    fontSize: 12,
    color: '#555',
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    alignSelf: 'flex-end'
  }

  return (
    <div style={wrapperStyle}>
      {/* ── 拆分行：Carton Qty / Sleeve Qty ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

        {/* Carton Qty 列 */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4, textAlign: 'center' }}>
            Carton Size
          </div>
          <Select
            placeholder="Carton Size"
            value={cartonQty}
            onChange={handleCartonQtyChange}
            onClear={handleCartonQtyClear}
            disabled={props.disabled || packagingData.length === 0}
            showSearch
            allowClear
            style={{ width: '100%' }}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
            }
          >
            {cartonQtyOptions.map(opt => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </div>

        {/* 分隔符 */}
        <span style={{ paddingTop: 20, color: '#bbb', fontSize: 16, userSelect: 'none' }}>
          /
        </span>

        {/* Sleeve Qty 列 */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4, textAlign: 'center' }}>
            Qty per Sleeve
          </div>
          <Select
            placeholder={cartonQty ? 'Qty per Sleeve' : '—'}
            value={sleeveQty}
            onChange={handleSleeveQtyChange}
            onClear={handleSleeveQtyClear}
            disabled={!cartonQty || props.disabled}
            showSearch
            allowClear
            style={{ width: '100%' }}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
            }
          >
            {sleeveQtyOptions.map(opt => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </div>

        {props.inlinePreview && previewLabel && (
          <div style={inlinePreviewStyle}>
            <span style={{ fontWeight: 600, color: '#1677ff' }}>
              {previewLabel}
            </span>
          </div>
        )}

      </div>

      {/* ── 预览：完整包装名称 ── */}
      {!props.inlinePreview && previewLabel && (
        <div style={previewStyle}>
          {/* <span style={{ color: '#888' }}>Packaging</span> */}
          <span style={{ fontWeight: 600, color: '#1677ff' }}>
            {previewLabel}
          </span>
        </div>
      )}
    </div>
  )
}

export default PackagingTypeCascadeSelect
